import { useState } from "react";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  Table,
  Space,
} from "antd";
import { Plus, Trash2 } from "lucide-react";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import SLAConfigurationService from "@/services/APIs/SLAConfigurationService";

interface HolidayListModalProps {
  open: boolean;
  onClose: (newId?: string) => void;
}

const HolidayListModal = ({ open, onClose }: HolidayListModalProps) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Watch holidays to calculate total
  const holidays = Form.useWatch("holidays", form) ?? [];
  const totalHolidays = holidays.length;

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const payload = {
        ...values,
        from_date: values.from_date?.format("YYYY-MM-DD"),
        to_date: values.to_date?.format("YYYY-MM-DD"),
        holidays: values.holidays?.map((h: any) => ({
          ...h,
          date: h.date?.format("YYYY-MM-DD"),
        })),
      };

      const response = await SLAConfigurationService.createHolidayList(payload);
      notify.success("Success", "Holiday List created successfully");
      onClose(response.id ?? response.value);
      form.resetFields();
    } catch (error: any) {
      notify.error("Error", error?.message ?? "Failed to create holiday list");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Create Holiday List"
      onCancel={() => onClose()}
      onOk={handleSubmit}
      confirmLoading={isSubmitting}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          holidays: [],
        }}
      >
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="Holiday List Name"
            name="name"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="e.g. India Holidays 2026" />
          </Form.Item>

          <Form.Item label="Total Holidays">
            <InputNumber
              value={totalHolidays}
              readOnly
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            label="From Date"
            name="from_date"
            rules={[{ required: true, message: "Required" }]}
          >
            <DatePicker format="DD-MM-YYYY" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="To Date"
            name="to_date"
            rules={[{ required: true, message: "Required" }]}
          >
            <DatePicker format="DD-MM-YYYY" style={{ width: "100%" }} />
          </Form.Item>
        </div>

        <div className="mt-4">
          <label className="mb-2 block font-medium">Holidays</label>
          <Form.List name="holidays">
            {(fields, { add, remove }) => (
              <Table
                dataSource={fields}
                pagination={false}
                rowKey="key"
                bordered
                size="small"
                columns={[
                  {
                    title: "No.",
                    key: "index",
                    width: 60,
                    align: "center",
                    render: (_, __, index) => index + 1,
                  },
                  {
                    title: "Date*",
                    key: "date",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "date"]}
                        rules={[{ required: true, message: "Required" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <DatePicker
                          format="DD-MM-YYYY"
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: "Description*",
                    key: "description",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "description"]}
                        rules={[{ required: true, message: "Required" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="Holiday description" />
                      </Form.Item>
                    ),
                  },
                  {
                    title: "Action",
                    key: "action",
                    width: 60,
                    align: "center",
                    render: (_, field) => (
                      <CustomButton
                        type="text"
                        danger
                        icon={<Trash2 size={16} />}
                        onClick={() => remove(field.name)}
                        title="Remove"
                      />
                    ),
                  },
                ]}
                footer={() => (
                  <Space>
                    <CustomButton
                      type="dashed"
                      onClick={() => add()}
                      icon={<Plus size={16} />}
                    >
                      Add row
                    </CustomButton>
                    <CustomButton
                      type="text"
                      onClick={() => form.setFieldValue("holidays", [])}
                    >
                      Clear Table
                    </CustomButton>
                  </Space>
                )}
              />
            )}
          </Form.List>
        </div>
      </Form>
    </Modal>
  );
};

export default HolidayListModal;
